<script setup lang="ts">
  import {storeToRefs} from 'pinia';
  import {useI18n} from 'vue-i18n';
  import {useErrorReportStore} from '@core/store/ErrorReportStore';
  import {
    List,
    ListItem,
    ListItemTitle,
    ListItemSubtitle,
  } from '@core/components/List';
  import {Modal} from '@core/components/Modal';
  import Button from '@core/components/Button.vue';
  import {
    Accordion,
    HorizontalAccordionItem,
    HorizontalAccordionItemTitle,
    HorizontalAccordionItemContent,
  } from '@core/components/Accordion';

  const errorReportStore = useErrorReportStore();
  const {t} = useI18n();

  const {errors} = storeToRefs(errorReportStore);

  async function submitErrorsAndReload() {
    try {
      const ok = await errorReportStore.submitErrors();
      if (ok) {
        errorReportStore.clearErrors();
        globalThis.location.href = '/';
      } else {
        console.error('Error submitting errors: request failed');
      }
    } catch (error) {
      console.error('Error submitting errors:', error);
    }
  }

  function closeAndContinue() {
    errorReportStore.clearErrors();
  }
</script>

<template>
  <Modal
    v-if="errors.length > 0"
    :model-value="true"
    :title="t('core.errorHappened')"
    :max-width="690"
    @close="closeAndContinue">
    <template #default>
      <p class="mt-4 mb-4">{{ t('core.sendErrors') }}</p>
      <Accordion>
        <HorizontalAccordionItem>
          <HorizontalAccordionItemTitle>
            <div class="flex items-center justify-between w-full">
              <span class="font-medium">
                {{ t('core.listOfErrorsThatHappened') }}
              </span>
            </div>
          </HorizontalAccordionItemTitle>
          <HorizontalAccordionItemContent>
            <List>
              <ListItem
                v-for="(error, index) in errors"
                :key="`${error.type}-${error.errorCode ?? 'no-code'}-${index}-${String(error.message ?? '').slice(0, 64)}`">
                <ListItemTitle>{{ error.message }}</ListItemTitle>
                <ListItemSubtitle :lines="3">
                  <p>
                    {{ t('core.errorType') }}:
                    {{ error.type }}
                  </p>
                  <p>
                    <span v-if="error.errorCode">
                      {{ t('core.errorCode') }}:
                      {{ error.errorCode }}
                    </span>
                    <span v-if="error.component">
                      |
                      {{ t('core.errorComponent') }}:
                      {{ error.component }}
                    </span>
                  </p>
                </ListItemSubtitle>
              </ListItem>
            </List>
          </HorizontalAccordionItemContent>
        </HorizontalAccordionItem>
      </Accordion>
    </template>
    <template #actions>
      <div class="flex flex-1 justify-end gap-2">
        <Button
          type="cancel"
          @click="closeAndContinue">
          {{ t('core.closeAndContinue') }}
        </Button>
        <Button
          type="submit"
          @click="submitErrorsAndReload">
          {{ t('core.submitErrorsAndReload') }}
        </Button>
      </div>
    </template>
  </Modal>
</template>
